import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useBlocker } from 'react-router-dom';
import { Modal } from '../components/Modal.js';

import { UnsavedContext, type Guard } from './unsaved-context.js';

/** Router navigation, local month changes and logout share the same guard. */
export function UnsavedChangesProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const guard = useRef<Guard | null>(null);
  const [pending, setPending] = useState<(() => void) | null>(null);
  const [working, setWorking] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const register = useCallback((value: Guard | null) => {
    guard.current = value;
    setSaving(value?.saving ?? false);
  }, []);
  const blocked = useCallback(() => Boolean(guard.current?.dirty || guard.current?.saving), []);
  const blocker = useBlocker(blocked);
  const request = useCallback((action: () => void) => {
    if (guard.current?.dirty || guard.current?.saving) {
      setFailed(false);
      setPending(() => action);
    } else action();
  }, []);
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent): void => {
      if (guard.current?.dirty || guard.current?.saving) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);
  const cancel = (): void => {
    setPending(null);
    setFailed(false);
    if (blocker.state === 'blocked') blocker.reset();
  };
  const proceed = (): void => {
    const action = pending;
    setPending(null);
    setFailed(false);
    if (blocker.state === 'blocked') blocker.proceed();
    else action?.();
  };
  const saveAndProceed = async (): Promise<void> => {
    setWorking(true);
    setFailed(false);
    try {
      if (await guard.current?.save()) proceed();
      else setFailed(true);
    } catch {
      setFailed(true);
    } finally {
      setWorking(false);
    }
  };
  const busy = working || saving;
  return (
    <UnsavedContext.Provider
      value={{
        register,
        request,
        navigationPending: pending !== null || blocker.state === 'blocked',
      }}
    >
      {children}
      {(pending !== null || blocker.state === 'blocked') && (
        <Modal title="保存していない変更があります" onClose={cancel} busy={busy}>
          <p className="text-sm mb-4">
            入力内容を保存してから移動しますか？ 日報は下書きとして保存します。
          </p>
          {busy && (
            <p role="status" className="mb-3 text-sm">
              保存中です。しばらくお待ちください。
            </p>
          )}
          {failed && (
            <p role="alert" className="mb-3 text-sm text-danger-600">
              保存できませんでした。画面に戻り、入力内容や保存エラーを確認してください。
            </p>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" className="btn btn--secondary" disabled={busy} onClick={cancel}>
              戻る
            </button>
            <button type="button" className="btn btn--ghost" disabled={busy} onClick={proceed}>
              変更を破棄して移動
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={() => void saveAndProceed()}
            >
              保存して移動
            </button>
          </div>
        </Modal>
      )}
    </UnsavedContext.Provider>
  );
}
